import { map } from "lodash";
import AppContext from "PFApp/app_context";
import { DropdownOption, GroupDropdownOption } from "PFComponents/dropdown/dropdown";
import { Select } from "PFComponents/select/select";
import { Typography } from "PFComponents/typography";
import useIsPermittedTo from "PFCore/helpers/use_is_permitted_to";
import { PermissionRule } from "PFTypes";
import React, { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { CalendarMetric, CalendarMode, CalendarView } from "../types";
import { useBookingsOverviewContext } from "./overview/context/bookings_overview_context";
import { useBookingModuleContext } from "./providers/booking_module_context_provider";
import css from "./toolbar.module.scss";

type BookingModuleView = CalendarView.BookingsOnly | CalendarView.Bookings | CalendarView.Compressed;

const CALENDAR_VIEWS_BY_MODE = {
  [CalendarMode.Workforce]: [
    CalendarView.WallPlanner,
    CalendarView.Bookings,
    CalendarView.BookingsOnly,
    CalendarView.Compressed
  ],
  [CalendarMode.Projects]: [CalendarView.EngagementsAndAuditEngagements, CalendarView.RolesAndAuditRoles]
};

const METRICS: CalendarMetric[] = [
  CalendarMetric.TotalHours,
  CalendarMetric.Availability,
  CalendarMetric.Utilization
];

type DisplayElementProps = {
  value: string;
  additionalInfo: string;
};

const DisplayElement = ({ value, additionalInfo }: DisplayElementProps) => (
  <>
    <Typography variant="bodyRegular" className={css.viewSelectorValue} noMargin>
      {value}
    </Typography>
    <Typography variant="bodyRegular" className={css.viewSelectorAdditionalInfo} noMargin>
      {additionalInfo}
    </Typography>
  </>
);

export const ViewAndMetricSelector = () => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const isPermittedTo = useIsPermittedTo(currentProfile);
  const { t } = useTranslation("bookingModule");
  const {
    calendarMode,
    showUtilizationMetrics,
    calendarData: { view, setView, metric, setMetric }
  } = useBookingsOverviewContext();
  const isWallPlannerAvailable = isPermittedTo(PermissionRule.WallPlannerAccess);

  const { headerDisabled } = useBookingModuleContext();

  const displayElements = useMemo(
    () => ({
      [CalendarView.EngagementsAndAuditEngagements]: (
        <DisplayElement
          value={t(`viewMode.engagementsAndAuditEngagements`)}
          additionalInfo={t(`viewMode.engagementsAndAuditEngagementsAdditionalInfo`)}
        />
      ),
      [CalendarView.RolesAndAuditRoles]: (
        <DisplayElement
          value={t(`viewMode.rolesAndAuditRoles`)}
          additionalInfo={t(`viewMode.rolesAndAuditRolesAdditionalInfo`)}
        />
      )
    }),
    [t]
  );

  const calendarViewOptions: DropdownOption[] = useMemo(
    () =>
      Object.values(CALENDAR_VIEWS_BY_MODE)
        .flat()
        .filter(
          (viewMode) =>
            CALENDAR_VIEWS_BY_MODE[calendarMode]?.includes(viewMode) &&
            (viewMode !== CalendarView.WallPlanner || isWallPlannerAvailable)
        )
        .map((viewMode) => ({
          id: viewMode,
          item: viewMode,
          displayElement: displayElements[viewMode] ?? t(`viewMode.${viewMode}`)
        })),
    [t, calendarMode, displayElements, isWallPlannerAvailable]
  );

  const metricOptions = useMemo(
    () =>
      map(METRICS, (metricMode) => ({
        id: metricMode,
        item: metricMode,
        displayElement: t(`metricMode.${metricMode}`)
      })).filter(({ id }) => showUtilizationMetrics || id !== CalendarMetric.Utilization),
    [showUtilizationMetrics]
  );

  const readableViewValue = t(`viewMode.${view as BookingModuleView}`);
  const readableMetricValue = ![CalendarView.BookingsOnly, CalendarView.Compressed].includes(view)
    ? t(`metricMode.${metric}`)
    : undefined;
  const readableValue = useMemo(() => {
    if (calendarMode === CalendarMode.Projects) {
      return readableViewValue;
    }
    return [readableViewValue, readableMetricValue].filter((value) => value).join(" - ");
  }, [calendarMode, readableViewValue, readableMetricValue]);

  const groupedOptions: GroupDropdownOption[] = useMemo(
    () => [
      {
        id: "view",
        label: "View",
        options: calendarViewOptions
      },
      {
        id: "metric",
        label: "Metric",
        options: metricOptions,
        disabled: [CalendarView.BookingsOnly, CalendarView.Compressed].includes(view)
      }
    ],
    [calendarViewOptions, metricOptions, view]
  );

  const handleChange = (selection: DropdownOption[], item: DropdownOption) => {
    const isSelectedOptionView = Object.values(CalendarView).includes(item.id as BookingModuleView);

    if (isSelectedOptionView && item.id !== view) {
      setView(item.id as CalendarView);
    } else if (!isSelectedOptionView && item.id !== metric) {
      setMetric(item.id as CalendarMetric);
    }
  };

  const selectedItems = [
    ...calendarViewOptions.filter(({ id }) => id === view),
    ...(![CalendarView.BookingsOnly, CalendarView.Compressed].includes(view)
      ? metricOptions.filter(({ id }) => id === metric)
      : [])
  ];

  return (
    <div>
      <Select
        // @ts-ignore
        readOnly
        disabled={headerDisabled}
        kind="bordered"
        value={readableValue}
        closeOnChange={calendarMode === CalendarMode.Projects}
        isMultiSelect
        controlledValue
        className={css.toolbarSelect}
        handleChange={handleChange}
        selectedItems={selectedItems}
        options={calendarMode === CalendarMode.Projects ? calendarViewOptions : groupedOptions}
        dropdownStyle={{ maxHeight: 300 }}
      />
    </div>
  );
};
